import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Check, ChevronDown, ArrowLeft, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL, formatApiError, FLOORING_TYPES, CONDITIONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { UnitToggle } from "@/components/UnitToggle";
import { mToUnit, unitToM, unitLabel, convertDisplay } from "@/lib/units";
import RetailerLayout from "@/components/RetailerLayout";
import PhotoStrip from "@/components/PhotoStrip";
import CameraCapture from "@/components/CameraCapture";
import { StockTypeSelect } from "@/components/StockTypeSelect";
import { PriceCompareHint } from "@/components/PriceCompareHint";

const BLANK = {
  photos: [],
  flooringType: "carpet",
  colour: "",
  texture: "",
  width: "",
  length: "",
  coveragePerPack: "",
  packsAvailable: "",
  priceBasis: "total_stock",
  price: "",
  rrp: "",
  condition: "new",
  manufacturer: "",
  rangeName: "",
  manufacturerColour: "",
  productCode: "",
  description: "",
  deliveryAvailable: false,
  stockType: "",
  deliveryFee: "",
};

// Categories that ship as packs/boxes/tiles — matching by coverage, not
// rectangular dimensions. Kept in sync with backend deps.COVERAGE_TYPES.
const COVERAGE_TYPES = new Set(["laminate", "lvt", "carpet_tiles"]);

/**
 * Fast mobile-first stock entry. Six required fields + an expandable "More
 * details" drawer. Two publish CTAs — normal Publish, and "Publish & Add
 * Another" for warehouse-walk onboarding sessions. When Admin is entering
 * stock on behalf of a retailer, the retailer_id sticks in the query string
 * across every subsequent add.
 */
export default function RetailerNewListing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const adminRetailerId = params.get("retailer_id"); // admin-on-behalf
  const [form, setForm] = useState(BLANK);
  const { unit } = useUnit();
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [carry, setCarry] = useState({ category: true, manufacturer: true }); // default carry-over
  const [retailerName, setRetailerName] = useState(null);
  const [addedCount, setAddedCount] = useState(0);
  const [smartOpen, setSmartOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [aiFields, setAiFields] = useState(new Set()); // field names filled by AI
  const [scanNote, setScanNote] = useState(null);
  const [lastScan, setLastScan] = useState(null); // raw AI suggestions (for correction history)
  const [confirmChecked, setConfirmChecked] = useState(false); // dims/qty/price confirmation gate

  const isAdminOnBehalf = user?.role === "admin" && !!adminRetailerId;
  const draftKey = `fm.newlisting.draft.${user?.id || "me"}.${adminRetailerId || "self"}`;

  // Restore an in-progress draft (survives AI failure or navigating away/back).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(draftKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.form) setForm({ ...BLANK, ...saved.form });
        if (Array.isArray(saved?.aiFields)) setAiFields(new Set(saved.aiFields));
        if (saved?.lastScan) setLastScan(saved.lastScan);
        if (saved?.scanNote) setScanNote(saved.scanNote);
      }
    } catch (e) { /* ignore corrupt draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Persist the draft as the retailer types. Cleared on successful publish.
  useEffect(() => {
    try {
      const isBlank = JSON.stringify(form) === JSON.stringify(BLANK) && !lastScan;
      if (isBlank) return;
      window.localStorage.setItem(draftKey, JSON.stringify({ form, aiFields: [...aiFields], lastScan, scanNote }));
    } catch (e) { /* storage full / unavailable — non-fatal */ }
  }, [form, aiFields, lastScan, scanNote, draftKey]);

  function clearDraft() {
    try { window.localStorage.removeItem(draftKey); } catch (e) { /* ignore */ }
  }

  useEffect(() => {
    if (isAdminOnBehalf) {
      api
        .get(`/admin/retailers/${adminRetailerId}`)
        .then((r) => setRetailerName(r.data.retailer?.name))
        .catch(() => setRetailerName(null));
    }
  }, [adminRetailerId, isAdminOnBehalf]);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function resetForNext() {
    // Preserve only what the user asked to carry over. Photos, dimensions,
    // price and product code MUST NOT persist across items — different piece.
    setForm((f) => ({
      ...BLANK,
      flooringType: carry.category ? f.flooringType : BLANK.flooringType,
      manufacturer: carry.manufacturer ? f.manufacturer : "",
      // Condition + description reset. Everything else clean.
    }));
    setError(null);
    setAiFields(new Set());
    setScanNote(null);
    setLastScan(null);
    setConfirmChecked(false);
    clearDraft();
    // Scroll back to top so the phone camera prompt is immediately visible.
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }

  /**
   * Handles the guided 3-shot camera flow:
   *  1. Upload all 3 photos to /api/upload/photo (as PhotoStrip normally does).
   *  2. Find the shot flagged with shotKey === "label" and POST it to
   *     /api/retailer/label-scan.
   *  3. Merge non-empty response fields into the form and remember which
   *     ones came from AI so the retailer sees a "please confirm" pill.
   */
  async function handleSmartShots(files) {
    setScanning(true);
    setError(null);
    setScanNote(null);
    try {
      // Upload each shot for the gallery; tag label/overall/texture shots so
      // we can also send them to the AI reader.
      const urls = [];
      const roleFiles = {}; // label | overall | texture
      for (const f of files) {
        if (f.shotKey === "label") roleFiles.label = f;
        else if (f.shotKey === "roll_end") roleFiles.overall = f;
        else if (f.shotKey === "close_up") roleFiles.texture = f;
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        urls.push(`${BACKEND_URL}${r.data.url}`);
      }
      setForm((s) => ({ ...s, photos: urls.slice(0, 5) }));

      // Run the AI scan across all available shots. Dimensions come from the
      // label only (enforced server-side). Failures degrade gracefully.
      if (roleFiles.label || roleFiles.overall || roleFiles.texture) {
        const fd = new FormData();
        if (roleFiles.label) fd.append("file", roleFiles.label);
        if (roleFiles.overall) fd.append("overall", roleFiles.overall);
        if (roleFiles.texture) fd.append("texture", roleFiles.texture);
        const r = await api.post("/retailer/label-scan", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        applyScan(r.data);
      } else {
        setScanNote("No label shot detected. Please type the details below.");
      }
    } catch (e) {
      // AI failed — the uploaded photos + any typed values are preserved.
      setScanNote(formatApiError(e) || "AI scan failed — your photos are saved. Please fill in the details manually.");
    } finally {
      setScanning(false);
    }
  }

  /**
   * Merge cleaned AI response into the form. Only fills fields that are
   * currently empty so retailer edits are never overwritten. Records
   * which fields came from AI for the "please confirm" pill.
   */
  function applyScan(result) {
    if (!result?.ok || !result.fields) {
      setScanNote(result?.reason || "AI couldn't read the label — please type the details.");
      return;
    }
    setLastScan(result); // keep original suggestions for correction history
    const filled = new Set(aiFields);
    let touchedCritical = false;
    setForm((f) => {
      const next = { ...f };
      const setIfEmpty = (key, val) => {
        if (val === undefined || val === null || val === "") return;
        if (next[key] !== "" && next[key] !== null && next[key] !== undefined) return;
        next[key] = String(val);
        filled.add(key);
      };
      setIfEmpty("colour", result.fields.colour || result.fields.colour_family);
      setIfEmpty("texture", result.fields.texture);
      setIfEmpty("manufacturer", result.fields.manufacturer);
      setIfEmpty("rangeName", result.fields.range_name);
      setIfEmpty("manufacturerColour", result.fields.colour);
      setIfEmpty("productCode", result.fields.product_code);
      if (result.fields.flooring_type && FLOORING_TYPES.some((t) => t.value === result.fields.flooring_type)) {
        if (next.flooringType === BLANK.flooringType) {
          next.flooringType = result.fields.flooring_type;
          filled.add("flooringType");
        }
      }
      // Dimensions/coverage ONLY arrive from the label (server-enforced) and
      // ALWAYS need confirmation before publishing.
      if (result.fields.width_m && !next.width) {
        next.width = String(mToUnit(result.fields.width_m, unit));
        filled.add("width"); touchedCritical = true;
      }
      if (result.fields.length_m && !next.length) {
        next.length = String(mToUnit(result.fields.length_m, unit));
        filled.add("length"); touchedCritical = true;
      }
      if (result.fields.pack_coverage_m2 && !next.coveragePerPack) {
        next.coveragePerPack = String(result.fields.pack_coverage_m2);
        filled.add("coveragePerPack"); touchedCritical = true;
      }
      return next;
    });
    setAiFields(filled);
    if (touchedCritical) setConfirmChecked(false); // force re-confirmation of AI dims
    setShowMore(true); // reveal the drawer so retailer can see brand/range fields
    const filledCount = filled.size;
    setScanNote(
      filledCount > 0
        ? `AI pre-filled ${filledCount} field${filledCount === 1 ? "" : "s"} — please review each (marked “AI · Confirm”) and confirm the dimensions, quantity and price before publishing.`
        : "AI couldn't confidently read this. Please fill in the details below.",
    );
  }

  async function publish({ addAnother }) {
    // Confirmation gate — dimensions/coverage, quantity and price must be
    // explicitly confirmed before anything goes live (required whether the
    // values were typed or AI-suggested).
    if (!confirmChecked) {
      setConfirmChecked(false);
      setError("Please confirm the dimensions, quantity and price are correct before publishing.");
      if (typeof window !== "undefined") window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const isCoverage = COVERAGE_TYPES.has(form.flooringType);
      const description =
        form.description.trim() ||
        (form.texture.trim() ? `Texture/finish: ${form.texture.trim()}.` : null);
      const payload = {
        flooring_type: form.flooringType,
        colour: form.colour.trim(),
        matching_mode: isCoverage ? "coverage" : "dimensions",
        price_gbp: parseFloat(form.price),
        rrp_gbp: form.rrp ? parseFloat(form.rrp) : null,
        condition: form.condition,
        photo_urls: form.photos,
        manufacturer: form.manufacturer.trim() || null,
        range_name: form.rangeName.trim() || null,
        manufacturer_colour: form.manufacturerColour.trim() || null,
        product_code: form.productCode.trim() || null,
        description,
        status: "live",
        ai_suggestions: lastScan ? { fields: lastScan.fields, confidence: lastScan.confidence, requires_confirmation: lastScan.requires_confirmation, texture_suggested: lastScan.fields?.texture || null } : null,
      };
      if (isCoverage) {
        payload.coverage_per_pack_m2 = parseFloat(form.coveragePerPack);
        payload.packs_available = parseInt(form.packsAvailable, 10);
        payload.price_basis = form.priceBasis || "total_stock";
      } else {
        payload.width_m = unitToM(form.width, unit);
        payload.length_m = unitToM(form.length, unit);
      }
      payload.delivery_available = !!form.deliveryAvailable;
      if (form.stockType) payload.stock_type = form.stockType;
      if (form.deliveryAvailable && form.deliveryFee) {
        payload.delivery_fee_gbp = parseFloat(form.deliveryFee);
      }
      const url = isAdminOnBehalf
        ? `/admin/retailers/${adminRetailerId}/listings`
        : "/retailer/listings";
      await api.post(url, payload);
      clearDraft();
      toast.success("Listing published", {
        description: `${form.colour} ${form.flooringType.replace("_", " ")} · £${form.price}`,
      });
      if (addAnother) {
        setAddedCount((n) => n + 1);
        resetForNext();
      } else if (isAdminOnBehalf) {
        nav(`/admin/retailers/${adminRetailerId}`);
      } else {
        nav("/retailer");
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const isCoverage = COVERAGE_TYPES.has(form.flooringType);
  const newBasis = !isCoverage ? "whole piece" : form.priceBasis === "per_pack" ? "per pack" : "all available stock";
  const area =
    !isCoverage && form.width && form.length
      ? (unitToM(form.width, unit) * unitToM(form.length, unit)).toFixed(2)
      : null;
  const totalCoverage =
    isCoverage && form.coveragePerPack && form.packsAvailable
      ? (parseFloat(form.coveragePerPack) * parseInt(form.packsAvailable, 10)).toFixed(2)
      : null;

  return (
    <RetailerLayout>
      <main className="max-w-2xl mx-auto px-3 sm:px-6 py-4 sm:py-6" data-testid="new-listing-page">
        {isAdminOnBehalf && (
          <div className="mb-3 flex items-center justify-between bg-[#2A5A3B]/5 border border-[#2A5A3B]/20 rounded-xl px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-[#2A5A3B] font-semibold">
                Adding stock for
              </div>
              <div className="font-semibold text-slate-900 truncate" data-testid="onbehalf-retailer">
                {retailerName || "Retailer"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => nav(`/admin/retailers/${adminRetailerId}`)}
              className="text-xs font-semibold text-slate-600 hover:text-[#E05915] inline-flex items-center gap-1"
              data-testid="back-to-retailer"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
          </div>
        )}

        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-5 h-5 text-[#E05915]" aria-hidden="true" />
          <h1 className="font-display font-bold text-2xl text-slate-900">Add a remnant</h1>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Photo · type · colour · width · length · price. That's it.
          {addedCount > 0 && (
            <span className="ml-1 text-[#2A5A3B] font-semibold" data-testid="session-count">
              · {addedCount} added this session
            </span>
          )}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            publish({ addAnother: false });
          }}
          className="space-y-4"
        >
          {/* Smart-shoot CTA — the fastest path to a listing. Three
              guided shots, AI pre-fills the form, retailer confirms. */}
          <div
            className="bg-gradient-to-br from-[#E05915] to-[#c24b10] text-white rounded-2xl p-4 sm:p-5 shadow-md"
            data-testid="smart-shoot-card"
          >
            <div className="flex items-start gap-3">
              <div className="grid place-items-center w-11 h-11 rounded-xl bg-white/20 shrink-0">
                <Wand2 className="w-5 h-5" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-bold text-white/80">
                  Smart shoot
                </div>
                <h3 className="font-display font-semibold text-lg leading-tight mt-0.5">
                  Take 3 guided shots — we'll fill the form for you
                </h3>
                <p className="text-sm text-white/90 mt-1">
                  Roll end · pile close-up · product label. FloorMatch reads the
                  label and pre-fills brand, range, colour and code.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSmartOpen(true)}
              disabled={scanning}
              className="mt-4 w-full h-12 rounded-full bg-white text-[#c24b10] font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-70"
              data-testid="smart-shoot-start"
            >
              <Sparkles className="w-4 h-4" />
              {scanning ? "Reading the label…" : "Start smart shoot"}
            </button>
            {scanNote && (
              <div
                className="mt-3 text-xs bg-white/15 border border-white/25 rounded-lg px-3 py-2"
                data-testid="scan-note"
              >
                {scanNote}
              </div>
            )}
          </div>

          {/* 1. Photo */}
          <Section n="1" label="Photo of the roll end">
            <PhotoStrip photos={form.photos} onChange={(p) => set("photos", p)} />
          </Section>

          {/* 2. Flooring type */}
          <Section n="2" label="Flooring type">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="flooring-type-grid">
              {FLOORING_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set("flooringType", t.value)}
                  className={`h-11 rounded-lg text-sm font-semibold border transition-colors ${
                    form.flooringType === t.value
                      ? "bg-[#2A5A3B] text-white border-[#2A5A3B]"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`ft-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Section>

          {/* 3. Colour */}
          <Section n="3" label="Colour" aiFilled={aiFields.has("colour")}>
            <input
              required
              value={form.colour}
              onChange={(e) => {
                set("colour", e.target.value);
                if (aiFields.has("colour")) {
                  const next = new Set(aiFields);
                  next.delete("colour");
                  setAiFields(next);
                }
              }}
              placeholder="e.g. Oatmeal, Charcoal Grey"
              className="fm-input"
              data-testid="new-colour"
            />
          </Section>

          {/* 4-5. Dimensions OR Coverage — dynamic by flooring type */}
          {!isCoverage ? (
            <Section n="4" label="Dimensions" aiFilled={aiFields.has("width") || aiFields.has("length")}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500">Enter in</span>
                <UnitToggle onSwitch={(from, to) => {
                  set("width", convertDisplay(form.width, from, to));
                  set("length", convertDisplay(form.length, from, to));
                }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <NumField
                  label={`Width (${unitLabel(unit)})`}
                  value={form.width}
                  onChange={(v) => set("width", v)}
                  testId="new-width"
                />
                <NumField
                  label={`Length (${unitLabel(unit)})`}
                  value={form.length}
                  onChange={(v) => set("length", v)}
                  testId="new-length"
                />
              </div>
              <div className="mt-2 text-sm text-slate-600" data-testid="area-preview">
                Area: <strong className="text-slate-900">{area ? `${area} m²` : "—"}</strong>
              </div>
            </Section>
          ) : (
            <Section n="4" label="Pack coverage" aiFilled={aiFields.has("coveragePerPack")}>
              <div className="grid grid-cols-2 gap-3">
                <NumField
                  label="Coverage per pack (m²)"
                  value={form.coveragePerPack}
                  onChange={(v) => set("coveragePerPack", v)}
                  testId="new-coverage"
                />
                <NumField
                  label="Packs / boxes available"
                  value={form.packsAvailable}
                  onChange={(v) => set("packsAvailable", v.replace(/[^0-9]/g, ""))}
                  testId="new-packs"
                />
              </div>
              <div className="mt-2 text-sm text-slate-600" data-testid="total-coverage-preview">
                Total available:{" "}
                <strong className="text-slate-900">
                  {totalCoverage ? `${totalCoverage} m²` : "—"}
                </strong>
              </div>
              <div className="mt-3">
                <label className="fm-label">Price applies to</label>
                <div className="flex flex-wrap gap-2">
                  <PriceBasisChip
                    on={form.priceBasis === "total_stock"}
                    onClick={() => set("priceBasis", "total_stock")}
                    testId="pb-total"
                  >
                    All available stock
                  </PriceBasisChip>
                  <PriceBasisChip
                    on={form.priceBasis === "per_pack"}
                    onClick={() => set("priceBasis", "per_pack")}
                    testId="pb-per-pack"
                  >
                    Per pack
                  </PriceBasisChip>
                </div>
              </div>
            </Section>
          )}

          {/* 6. Price */}
          <Section n="5" label="Price (£)">
            <div className="grid grid-cols-2 gap-3">
              <NumField
                prefix="£"
                label={`Your selling price (${newBasis})`}
                value={form.price}
                onChange={(v) => set("price", v)}
                testId="new-price"
                required
              />
              <NumField
                prefix="£"
                label={`Retail price (${newBasis}, optional)`}
                value={form.rrp}
                onChange={(v) => set("rrp", v)}
                testId="new-rrp"
              />
            </div>
            <PriceCompareHint price={form.price} retail={form.rrp} basis={newBasis} />
            <div className="mt-4">
              <StockTypeSelect value={form.stockType} onChange={(v) => set("stockType", v)} testId="new-stock-type" />
            </div>
          </Section>

          <Section n="6" label="Collection & delivery (optional)">
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.deliveryAvailable}
                onChange={(e) => set("deliveryAvailable", e.target.checked)}
                className="mt-1 accent-[#E05915]"
                data-testid="new-delivery-available"
              />
              <span>
                <span className="font-semibold text-slate-900">Offer collection &amp; delivery</span> —
                a driver brings it to the customer. The delivery fee is paid directly to the driver.
              </span>
            </label>
            {form.deliveryAvailable && (
              <div className="mt-3 max-w-[12rem]">
                <NumField
                  prefix="£"
                  label="Delivery fee"
                  value={form.deliveryFee}
                  onChange={(v) => set("deliveryFee", v)}
                  testId="new-delivery-fee"
                />
              </div>
            )}
          </Section>

          {/* Expandable optional details */}
          <button
            type="button"
            onClick={() => setShowMore((s) => !s)}
            className="w-full flex items-center justify-between text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:bg-slate-50"
            data-testid="toggle-more"
          >
            <span>More details {showMore ? "" : "(optional)"}</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showMore ? "rotate-180" : ""}`}
            />
          </button>
          {showMore && (
            <div className="space-y-3 bg-white border border-slate-200 rounded-xl p-4" data-testid="more-details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField
                  label="Manufacturer / brand"
                  value={form.manufacturer}
                  onChange={(v) => set("manufacturer", v)}
                  testId="new-manufacturer"
                  aiFilled={aiFields.has("manufacturer")}
                />
                <TextField
                  label="Range"
                  value={form.rangeName}
                  onChange={(v) => set("rangeName", v)}
                  testId="new-range"
                  aiFilled={aiFields.has("rangeName")}
                />
                <TextField
                  label="Manufacturer's colour"
                  value={form.manufacturerColour}
                  onChange={(v) => set("manufacturerColour", v)}
                  testId="new-mcolour"
                  aiFilled={aiFields.has("manufacturerColour")}
                />
                <TextField
                  label="Product / reference code"
                  value={form.productCode}
                  onChange={(v) => set("productCode", v)}
                  testId="new-productcode"
                  aiFilled={aiFields.has("productCode")}
                />
                <TextField
                  label="Texture / finish"
                  value={form.texture}
                  onChange={(v) => {
                    set("texture", v);
                    if (aiFields.has("texture")) { const n = new Set(aiFields); n.delete("texture"); setAiFields(n); }
                  }}
                  testId="new-texture"
                  aiFilled={aiFields.has("texture")}
                />
              </div>
              <div>
                <label className="fm-label">Condition</label>
                <select
                  value={form.condition}
                  onChange={(e) => set("condition", e.target.value)}
                  className="fm-input"
                  data-testid="new-condition"
                >
                  {CONDITIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="fm-label">Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  className="fm-input min-h-[80px]"
                  placeholder="Optional — we'll auto-generate one if left blank"
                  data-testid="new-description"
                />
              </div>
            </div>
          )}

          {/* Carry-over toggle bar */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs">
            <div className="text-slate-600 mb-2">
              When you tap <strong>Publish & Add Another</strong>, keep these fields:
            </div>
            <div className="flex flex-wrap gap-2">
              <CarryChip
                on={carry.category}
                onClick={() => setCarry((c) => ({ ...c, category: !c.category }))}
                testId="carry-category"
              >
                Category
              </CarryChip>
              <CarryChip
                on={carry.manufacturer}
                onClick={() => setCarry((c) => ({ ...c, manufacturer: !c.manufacturer }))}
                testId="carry-manufacturer"
              >
                Manufacturer
              </CarryChip>
              <span className="text-slate-400 text-[11px] self-center">
                · Photos, dimensions, price never carry over
              </span>
            </div>
          </div>

          {/* Confirmation gate — dimensions, quantity and price must be
              explicitly confirmed before publishing (required for both typed
              and AI-suggested values). */}
          <label
            className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
              confirmChecked ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"
            }`}
            data-testid="confirm-gate"
          >
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              className="mt-0.5 w-5 h-5 shrink-0 accent-[#2A5A3B]"
              data-testid="confirm-checkbox"
            />
            <span className="text-sm text-slate-700">
              I confirm the <strong>{isCoverage ? "coverage, packs available" : "width & length"}</strong>, <strong>price</strong>
              {" "}and quantity are correct. AI suggestions are a starting point only — never published without my check.
            </span>
          </label>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm" data-testid="submit-error">
              {error}
            </div>
          )}

          {/* Sticky action bar on mobile so it's always reachable */}
          <div className="sticky bottom-0 -mx-3 sm:mx-0 bg-white/95 backdrop-blur border-t sm:border sm:rounded-2xl border-slate-200 p-3 sm:p-4 flex flex-col sm:flex-row gap-2 z-20">
            <button
              type="button"
              disabled={submitting || !confirmChecked}
              onClick={() => publish({ addAnother: true })}
              className="fm-btn-primary flex-1 order-1 sm:order-2 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="publish-add-another"
            >
              <Check className="w-4 h-4" /> {submitting ? "Publishing…" : "Publish & Add Another"}
            </button>
            <button
              type="submit"
              disabled={submitting || !confirmChecked}
              className="h-11 sm:h-12 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-slate-700 px-4 flex-1 order-2 sm:order-1 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              data-testid="publish-btn"
            >
              {submitting ? "Publishing…" : "Publish"}
            </button>
          </div>
        </form>

        <CameraCapture
          open={smartOpen}
          guided
          onClose={() => setSmartOpen(false)}
          onCapture={(files) => {
            setSmartOpen(false);
            handleSmartShots(files);
          }}
        />
      </main>
    </RetailerLayout>
  );
}

function Section({ n, label, children, aiFilled }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-6 h-6 rounded-full bg-[#E05915]/10 text-[#E05915] text-xs font-bold">
            {n}
          </span>
          <span className="text-sm font-semibold text-slate-900">{label}</span>
        </div>
        {aiFilled && <AiFilledPill />}
      </div>
      {children}
    </section>
  );
}

function AiFilledPill() {
  return (
    <span
      className="inline-flex items-center gap-1 bg-[#E05915]/10 text-[#E05915] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
      data-testid="ai-filled-pill"
    >
      <Sparkles className="w-3 h-3" /> AI · Confirm
    </span>
  );
}

function NumField({ label, value, onChange, testId, prefix, required }) {
  return (
    <label className="block">
      {label && <span className="fm-label">{label}</span>}
      <div className="relative">
        {prefix && (
          <span className="absolute inset-y-0 left-3 grid place-items-center text-slate-500 pointer-events-none">
            {prefix}
          </span>
        )}
        <input
          type="text"
          inputMode="decimal"
          pattern="[0-9]*\.?[0-9]*"
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
          className={`fm-input ${prefix ? "pl-7" : ""}`}
          data-testid={testId}
        />
      </div>
    </label>
  );
}

function TextField({ label, value, onChange, testId, aiFilled }) {
  return (
    <label className="block">
      <span className="fm-label flex items-center justify-between">
        <span>{label}</span>
        {aiFilled && <AiFilledPill />}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`fm-input ${aiFilled ? "ring-1 ring-[#E05915]/40" : ""}`}
        data-testid={testId}
      />
    </label>
  );
}

function CarryChip({ on, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full border text-xs font-semibold transition-colors ${
        on
          ? "bg-[#2A5A3B] text-white border-[#2A5A3B]"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
      }`}
      data-testid={testId}
    >
      {on ? "✓ " : ""}
      {children}
    </button>
  );
}

function PriceBasisChip({ on, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
        on
          ? "bg-[#E05915] text-white border-[#E05915]"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
